import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  Update,
  Restaurant,
  TableRestaurant,
  Phone,
  Person,
  AccessTime,
  Payment,
  CheckCircle,
  Pending,
  LocalShipping,
  Store,
  Download,
} from '@mui/icons-material';
import { orderService } from '../../services/orderService';
import { useNotification } from '../../contexts/NotificationContext';
import invoiceService from '../../services/invoiceService';
import outletService from '../../services/outletService';

const TableOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [outletFilter, setOutletFilter] = useState('all');
  const [outlets, setOutlets] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();

  const orderStatuses = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'approved', label: 'Approved', color: 'info' },
    { value: 'preparing', label: 'Preparing', color: 'primary' },
    { value: 'ready', label: 'Ready', color: 'success' },
    { value: 'delivered', label: 'Delivered', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' },
  ];

  useEffect(() => {
    fetchOrders();
    fetchOutlets();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getOrders();
      // Filter only table orders (dine_in)
      const tableOrders = response.filter(order => order.order_type === 'dine_in');
      setOrders(tableOrders);
    } catch (error) {
      // console.error('Error fetching table orders:', error);
      setError('Failed to fetch table orders');
      showError('Failed to fetch table orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutlets = async () => {
    try {
      const response = await outletService.getOutlets()
        // const outletsData = await response.json();
        setOutlets(response);
      
    } catch (error) {
      // console.error('Error fetching outlets:', error);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDialog(true);
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setUpdateDialog(true);
  };

  const handleDownloadInvoice = async (order) => {
    try {
      // Get outlet information for the order
      const outlet = outlets.find(o => o.id === order.outlet_id) || {
        name: order.outlet_name || 'Main Outlet',
        address: 'Restaurant Address',
        phone: '+65 1234 5678',
        email: 'info@restaurant.com'
      };

      // Generate kitchen invoice PDF
      const pdfBlob = await invoiceService.generateKitchenInvoicePDF(order, outlet);
      
      // Download the PDF
      const filename = `Kitchen_Invoice_${order.order_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      invoiceService.downloadPDF(pdfBlob, filename);
      
      showSuccess('Kitchen invoice downloaded successfully');
    } catch (error) {
      // console.error('Error downloading invoice:', error);
      showError('Failed to download invoice');
    }
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !newStatus) return;

    try {
      setUpdating(true);
      await orderService.updateOrderStatus(selectedOrder.id, newStatus);
      showSuccess('Order status updated successfully');
      setUpdateDialog(false);
      fetchOrders();
    } catch (error) {
      // console.error('Error updating order status:', error);
      showError('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };
  // console.log("Outlets => ", outlets);
  const getStatusColor = (status) => {
    const statusObj = orderStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusObj = orderStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'pay_online':
        return '💳';
      case 'cash_on_delivery':
        return '💵';
      default:
        return '💳';
    }
  };

  const formatCurrency = (amount) => {
    return `S$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone?.includes(searchTerm) ||
      order.table?.table_number?.toString().includes(searchTerm) ||
      order.outlet_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesOutlet = outletFilter === 'all' || order.outlet_id?.toString() === outletFilter;
    
    return matchesSearch && matchesStatus && matchesOutlet;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}>
          Table Order Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          Manage dine-in orders from QR code scanning
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TableRestaurant sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {orders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Pending sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {orders.filter(o => o.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {orders.filter(o => o.status === 'ready').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ready
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                placeholder="Search by order number, customer name, phone, table number, or outlet"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {orderStatuses.map(status => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Outlet Filter</InputLabel>
                <Select
                  value={outletFilter}
                  onChange={(e) => setOutletFilter(e.target.value)}
                  label="Outlet Filter"
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                >
                  <MenuItem value="all">All Outlets</MenuItem>
                  {outlets.map(outlet => (
                    <MenuItem key={outlet.id} value={outlet.id.toString()}>
                      {outlet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={fetchOrders}
                startIcon={<Update />}
                fullWidth
                size={isMobile ? "small" : "medium"}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
            },
          }}
        >
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '100px', sm: '120px' },
                  whiteSpace: 'nowrap'
                }}>
                  Order #
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '80px', sm: '100px' },
                  whiteSpace: 'nowrap'
                }}>
                  Table
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '120px', sm: '150px' },
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', md: 'table-cell' }
                }}>
                  Outlet
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '120px', sm: '150px' },
                  whiteSpace: 'nowrap'
                }}>
                  Customer
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '100px', sm: '120px' },
                  whiteSpace: 'nowrap'
                }}>
                  Phone
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '80px', sm: '100px' },
                  whiteSpace: 'nowrap'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '120px', sm: '150px' },
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'table-cell' }
                }}>
                  Time
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  minWidth: { xs: '100px', sm: '120px' },
                  whiteSpace: 'nowrap'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Typography variant="body2" fontWeight="bold" sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      #{order.order_number}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableRestaurant sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'primary.main' 
                      }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {order.table?.table_number ? `Table ${order.table.name}` : `Table ${order.table_id}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Store sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'text.secondary' 
                      }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {order.outlet_name || order.outlet?.name || 'Unknown Outlet'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'text.secondary' 
                      }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {order.customer_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'text.secondary' 
                      }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {order.customer_phone}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Chip
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status)}
                      size="small"
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: '20px', sm: '24px' }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'text.secondary' 
                      }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatDate(order.created_at)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 0.5, sm: 1 },
                      flexWrap: 'nowrap'
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewOrder(order)}
                        color="primary"
                        sx={{ 
                          minWidth: { xs: '32px', sm: '40px' },
                          height: { xs: '32px', sm: '40px' }
                        }}
                      >
                        <Visibility sx={{ fontSize: { xs: 16, sm: 20 } }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateStatus(order)}
                        color="secondary"
                        sx={{ 
                          minWidth: { xs: '32px', sm: '40px' },
                          height: { xs: '32px', sm: '40px' }
                        }}
                      >
                        <Update sx={{ fontSize: { xs: 16, sm: 20 } }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadInvoice(order)}
                        color="success"
                        sx={{ 
                          minWidth: { xs: '32px', sm: '40px' },
                          height: { xs: '32px', sm: '40px' }
                        }}
                        title="Download Kitchen Invoice"
                      >
                        <Download sx={{ fontSize: { xs: 16, sm: 20 } }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={orderDialog}
        onClose={() => setOrderDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Restaurant sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Order #{selectedOrder?.order_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedOrder?.table?.table_number ? `Table ${selectedOrder.table.table_number}` : `Table ${selectedOrder?.table_id}`} • {selectedOrder?.outlet_name || selectedOrder?.outlet?.name || 'Unknown Outlet'} • {formatDate(selectedOrder?.created_at)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                Order Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedOrder?.customer_name}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Phone Number:</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedOrder?.customer_phone}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Table Number:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedOrder?.table?.table_number ? `Table ${selectedOrder.table.table_number}` : `Table ${selectedOrder?.table_id}`}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Outlet:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedOrder?.outlet_name || selectedOrder?.outlet?.name || 'Unknown Outlet'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip
                  label={getStatusLabel(selectedOrder?.status)}
                  color={getStatusColor(selectedOrder?.status)}
                  size="small"
                />
              </Box>

            </Grid>



            {/* Order Items */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Order Items
              </Typography>
              {selectedOrder?.order_items && selectedOrder.order_items.length > 0 ? (
                <List dense>
                  {selectedOrder.order_items.map((item, index) => (
                    <ListItem key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" fontWeight="bold">
                              {item.menu_item?.name || 'Unknown Item'}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {formatCurrency(item.total_price)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2">Quantity: {item.quantity}</Typography>
                            {item.size_name && (
                              <Typography variant="body2">
                                Size: {item.size_name} (+{formatCurrency(item.size_price)})
                              </Typography>
                            )}
                            {item.base_price && (
                              <Typography variant="body2">
                                Base Price: {formatCurrency(item.base_price)}
                              </Typography>
                            )}
                            {item.special_instructions && (
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                Special Instructions: {item.special_instructions}
                              </Typography>
                            )}
                            {item.menu_item?.description && (
                              <Typography variant="body2" color="text.secondary">
                                {item.menu_item.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No items found for this order.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOrderDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog
        open={updateDialog}
        onClose={() => setUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Order #{selectedOrder?.order_number} - {selectedOrder?.table?.table_number ? `Table ${selectedOrder.table.table_number}` : `Table ${selectedOrder?.table_id}`} - {selectedOrder?.outlet_name || selectedOrder?.outlet?.name || 'Unknown Outlet'}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="New Status"
              >
                {orderStatuses.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={updateOrderStatus}
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : <Update />}
          >
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableOrderManagement;
